// Full i3 Quality Manual & Interventional Radiology Framework content.
// Sourced from the document owner (Medical Director). Keep in sync with the
// governing record — bump `version` and `effectiveDate` on each material update.

export const QUALITY_MANUAL = {
  title: 'Quality Manual & Interventional Radiology Framework',
  org: 'i3 Radiology — Infinity Imaging & Interventions',
  version: '1.0',
  effectiveDate: '2026-02-20',
  reviewDate: '2027-02-20',
  owner: 'Medical Director',
  governanceLead: 'Practice Manager / Infection Prevention Lead',
  sections: [
    {
      id: 'purpose',
      n: '1',
      title: 'Purpose',
      body: `This Quality Manual establishes the clinical governance, infection prevention, procedural safety, and operational framework for image-guided interventional procedures performed at i3 Radiology.

The manual ensures that all procedures are conducted in a **safe, consistent, and evidence-based** manner, aligned with:

- Diagnostic Imaging Accreditation Scheme (**DIAS**)
- Standards expected by the **Royal Australian and New Zealand College of Radiologists (RANZCR)**
- Safety and quality principles consistent with the **Australian Commission on Safety and Quality in Health Care**`,
    },
    {
      id: 'scope',
      n: '2',
      title: 'Scope',
      body: `This manual applies to **all staff** involved in interventional radiology services, including:

- Radiologists
- Radiographers
- Sonographers
- Administrative staff
- Clinical support staff

**Procedures covered:**

- CT-guided interventions
- Ultrasound-guided interventions
- Injections, aspirations, biopsies, and related procedures`,
    },
    {
      id: 'governance',
      n: '3',
      title: 'Governance Framework',
      body: `### 3.1 Clinical Governance Structure

The practice maintains a structured governance model:

- **Medical Director** — overall clinical responsibility, governance oversight, incident review.
- **Radiologists** — procedural responsibility, clinical decision-making, emergency management.
- **Radiographers / Sonographers** — patient preparation, equipment readiness, procedural assistance, first-line emergency response.
- **Practice Manager / Infection Control Lead** — risk register maintenance, audit oversight, infection control compliance.

### 3.2 Risk Register

A formal risk register is maintained and **reviewed quarterly**.

Includes:
- Infection events
- Procedural complications
- Contrast reactions (including anaphylaxis)
- Near misses
- Equipment failures

Each entry must include:
- Incident description
- Root cause analysis
- Corrective actions
- Staff retraining (if required)
- Governance review sign-off

### 3.3 Incident Reporting

All incidents must be:

1. Documented using the incident reporting form.
2. Reviewed by the Medical Director.
3. Logged in the complication register where applicable.`,
    },
    {
      id: 'safety',
      n: '4',
      title: 'Patient Safety Systems',
      body: `### 4.1 Patient Identification

- Minimum **two identifiers** required.
- Confirmed at **three points**: Booking, Arrival, Pre-procedure.

### 4.2 Informed Consent

- **Written consent is required** for all procedures.
- Must include: **Risks · Benefits · Alternatives**.

### 4.3 Procedural Time-Out

A mandatory time-out must confirm:

1. Patient identity
2. Procedure type
3. Laterality
4. Allergies
5. Planned injectate`,
    },
    {
      id: 'procedures',
      n: '5',
      title: 'Interventional Procedure Framework',
      body: `### 5.1 CT-Guided Procedures

**Preparation**

- CT table cleaned
- Disposable paper sheet applied
- Sterile equipment prepared
- Emergency equipment available

**Sterile Technique**

- Radiographer opens sterile set using **yellow forceps only**.
- Radiologist dons sterile gloves **before** handling sterile contents.

**Gantry Safety Protocol**

For all needle manipulation:

\`Scan → Move patient out → Manipulate → Re-scan\`

This reduces contamination risk and preserves sterile technique.

**Medication Handling**

- Medications drawn under sterile conditions.
- **Preloaded syringes opened only after correct needle placement confirmed.**

### 5.2 Ultrasound-Guided Procedures

- Real-time imaging guidance
- Sterile probe preparation
- Identical antisepsis and no-touch technique`,
    },
    {
      id: 'infection',
      n: '6',
      title: 'Infection Prevention and Control',
      body: `### 6.1 Standard Antiseptic Protocol

The standard antiseptic used is:

> **2% chlorhexidine gluconate in 70% isopropyl alcohol**

Rationale:
- Rapid bactericidal action
- Sustained antimicrobial effect
- Alignment with invasive procedural standards

### 6.2 Application Technique

- "Sunrise" outward circular motion
- Adequate coverage
- Strict no-touch technique
- **Full air drying before needle entry**

### 6.3 Hybrid Antisepsis Model

**Single-Use Applicators (Mandatory)** — used for:

- Spinal procedures
- Nerve root injections
- Biopsies
- High-risk patients

**Multi-Use Bottles (Restricted)** — permitted only for low-risk procedures.

Requirements:
- Label with: Date opened · Staff initials · Discard date
- **Maximum duration: 14 days**

**Immediate discard if:**
- Contaminated
- Dropped
- Label missing

### 6.4 Environmental Cleaning

After each procedure:

1. CT table / ultrasound bed cleaned.
2. High-touch surfaces disinfected.
3. Procedure tray surface cleaned using chlorhexidine-based solution.
4. Disposable coverings replaced.`,
    },
    {
      id: 'post-procedure',
      n: '7',
      title: 'Post-Procedure Care',
      body: `### 7.1 Observation

- Routine procedures: discharge when stable.
- **Spinal / nerve procedures: minimum 20-minute observation.**

### 7.2 Patient Instructions

Provide:
- Written aftercare advice
- Red-flag symptoms
- Follow-up guidance

### 7.3 48-Hour Follow-Up

- Reception staff contact patient ~48 hours post-procedure.
- Call framed as a courtesy check.
- Concerning symptoms escalated to radiologist.

**All calls documented.**`,
    },
    {
      id: 'emergency',
      n: '8',
      title: 'Emergency Management',
      body: `### 8.1 Anaphylaxis Protocol

A standardised protocol is maintained and **displayed in procedure rooms**.

Includes:

1. Immediate recognition
2. Intramuscular adrenaline administration
3. Emergency escalation
4. Transfer to hospital

### 8.2 Emergency Equipment

- Fully stocked crash cart
- **Checked daily**
- Signed checklist maintained`,
    },
    {
      id: 'audit',
      n: '9',
      title: 'Audit and Quality Assurance',
      body: `### 9.1 Internal Audits

| Frequency | Audit |
|-----------|-------|
| Monthly   | Sterile technique |
| Monthly   | Cleaning compliance |
| Monthly   | Antisepsis compliance |
| Quarterly | Infection control review |
| Annual    | Full governance review |

### 9.2 External Audits

- Independent infection control audit (**recommended annually**).
- Findings documented and actioned.`,
    },
    {
      id: 'training',
      n: '10',
      title: 'Staff Training and Competency',
      body: `All clinical staff must:

- Complete **annual competency assessment**.
- Demonstrate proficiency in:
  - Aseptic technique
  - Antisepsis protocols
  - Emergency response`,
    },
    {
      id: 'documentation',
      n: '11',
      title: 'Documentation',
      body: `The practice maintains:

- Procedure records
- Consent forms
- Audit logs
- Incident reports
- Staff training records

All documentation must be **accurate · complete · audit-ready**.`,
    },
    {
      id: 'indicators',
      n: '12',
      title: 'Quality Indicators',
      body: `The practice monitors:

- Complication rates
- Infection rates
- Compliance with SOPs
- Follow-up completion`,
    },
    {
      id: 'dias',
      n: '13',
      title: 'DIAS Standard Mapping',
      body: `| Standard | Coverage |
|----------|----------|
| **1 — Governance** | Defined roles, risk register, audit systems |
| **2 — Patient Identification** | Two identifiers, time-out protocol |
| **3 — Procedures** | Standardised CT and US workflows |
| **4 — Medication Safety** | Controlled injectate handling, anaphylaxis protocol |
| **5 — Infection Control** | Chlorhexidine standard, hybrid antisepsis, cleaning |
| **6 — Incident Management** | Incident reporting, follow-up, audit cycle |`,
    },
    {
      id: 'final',
      n: '14',
      title: 'Final Statement',
      body: `This Quality Manual demonstrates that **i3 Radiology operates within a structured, auditable, and patient-centred clinical governance framework**, aligned with Australian accreditation standards and best-practice radiology principles.`,
    },
  ],
};
